/**
 * System Service Bridge
 * Abstracts communication between the React frontend and the Desktop shell (Tauri/Electron).
 */

import { apiFetch } from './apiClient';

export interface CommandResponse {
  success: boolean;
  output: string;
  error?: string;
  exitCode?: number;
}

export interface TempReading {
  label: string;
  celsius: number;
}

export interface SysDiskStat {
  mount: string;
  used_gb: number;
  total_gb: number;
  free_gb: number;
  percent: number;
}

export interface SysNetStat {
  rx_bytes: number;
  tx_bytes: number;
}

export interface SysDockerContainer {
  name: string;
  image: string;
  cpu_percent: string;
  mem_usage: string;
  uptime: string;
}

export interface SysDockerInfo {
  containers: SysDockerContainer[];
  count: number;
}

export interface SysSwapStat {
  used_gb: number;
  total_gb: number;
  percent: number;
}

export interface LiveStats {
  cpu_percent: number;
  memory_used_gb: number;
  memory_total_gb: number;
  memory_percent: number;
  temperatures: TempReading[];
  fan_speed_rpm: number | null;
  disk: SysDiskStat[];
  loadavg: [number, number, number];
  net: SysNetStat;
  swap: SysSwapStat;
  hostname: string;
  uptime_seconds: number;
  cpu_count: number;
  platform: string;
  release: string;
  arch: string;
  docker: SysDockerInfo | null;
}

class SystemService {
  private isTauri: boolean;
  private isElectron: boolean;

  constructor() {
    this.isTauri = typeof window !== 'undefined' && (!!(window as any).__TAURI_INTERNALS__ || !!(window as any).__TAURI_IPC__ || !!(window as any).__TAURI__);
    this.isElectron = typeof window !== 'undefined' && (!!(window as any).xfElectron || navigator.userAgent.toLowerCase().includes('electron'));
  }

  /**
   * Execute a system command
   */
  async runCommand(command: string): Promise<CommandResponse> {
    if (this.isTauri) {
      try {
        // Tauri 'run_command' would be a custom command defined in src-tauri/src/main.rs
        const { invoke } = await import('@tauri-apps/api/core');
        return await invoke('run_command', { command, cwd: null });
      } catch (err) {
        return { success: false, output: '', error: String(err) };
      }
    }

    if (this.isElectron && (window as any).xfElectron) {
      return await (window as any).xfElectron.runCommand(command);
    }

    // Web: no shell access
    return {
      success: false,
      output: '',
      error: 'System commands require the desktop app (Tauri). Browser mode has no shell access.',
    };
  }
  async getVolume(): Promise<number> {
    if (this.isTauri) {
      try {
        const { invoke } = await import('@tauri-apps/api/core');
        return await invoke<number>('get_system_volume');
      } catch { /* fallback */ }
    }
    return parseFloat(localStorage.getItem('xf_volume') || '50');
  }

  async setVolume(level: number): Promise<void> {
    localStorage.setItem('xf_volume', String(level));
    document.documentElement.style.setProperty('--xf-volume', String(level));
    if (this.isTauri) {
      try {
        const { invoke } = await import('@tauri-apps/api/core');
        await invoke('set_system_volume', { level });
      } catch { /* web fallback */ }
    }
  }

  async getBrightness(): Promise<number> {
    if (this.isTauri) {
      try {
        const { invoke } = await import('@tauri-apps/api/core');
        return await invoke<number>('get_screen_brightness');
      } catch { /* fallback */ }
    }
    return parseFloat(localStorage.getItem('xf_brightness') || '85');
  }

  async setBrightness(level: number): Promise<void> {
    localStorage.setItem('xf_brightness', String(level));
    if (this.isTauri) {
      try {
        const { invoke } = await import('@tauri-apps/api/core');
        await invoke('set_screen_brightness', { level });
      } catch { /* web fallback */ }
    }
  }

  /**
   * Toggle wallpaper visual mode + OS-level click-through (Win32 WS_EX_TRANSPARENT)
   */
  async setWallpaperMode(enabled: boolean): Promise<void> {
    if (enabled) {
      document.documentElement.classList.add('xf-wallpaper-mode');
    } else {
      document.documentElement.classList.remove('xf-wallpaper-mode');
    }

    if (this.isTauri) {
      try {
        const { invoke } = await import('@tauri-apps/api/core');
        await invoke('set_wallpaper_mode', { enabled });
      } catch (err) {
        console.error('Failed to set wallpaper mode:', err);
      }
    }
  }

  /**
   * Get system info (CPU, RAM, etc)
   */
  async getSystemStats(): Promise<any> {
    if (this.isTauri) {
      try {
        const { invoke } = await import('@tauri-apps/api/core');
        return await invoke('get_system_info');
      } catch (err) {
        console.error("Failed to get system stats:", err);
        return { cpu: 0, ram: 'N/A', disk: 'N/A' };
      }
    }
    if (this.isElectron) {
      return { cpu: 'N/A', ram: 'N/A', disk: 'N/A', platform: 'electron' };
    }
    // Web: use browser APIs where available
    const nav = navigator as any;
    return {
      cpu: nav.hardwareConcurrency || 'unknown',
      ram: nav.deviceMemory ? `${nav.deviceMemory}GB` : 'unknown',
      platform: navigator.platform,
      userAgent: navigator.userAgent.slice(0, 80),
    };
  }

  /**
   * Get live system stats with CPU%, GPU, temperatures
   */
  async getLiveStats(): Promise<LiveStats> {
    // 神经合成面板统一抓取部署服务器真实资源（不再读本机 PC）
    return this.getServerStats();
  }

  /**
   * Fallback: get system stats from Express server (works in web/dev mode)
   */
  async getServerStats(): Promise<LiveStats> {
    try {
      const res = await apiFetch('/api/system/stats');
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const s = await res.json();
      return normalizeSystemStats(s);
    } catch {
      return emptyLiveStats();
    }
  }
}

export const systemService = new SystemService();

export function emptyLiveStats(): LiveStats {
  return {
    cpu_percent: 0,
    memory_used_gb: 0,
    memory_total_gb: 0,
    memory_percent: 0,
    cpu_count: 0,
    temperatures: [],
    fan_speed_rpm: null,
    disk: [],
    loadavg: [0, 0, 0],
    net: { rx_bytes: 0, tx_bytes: 0 },
    swap: { used_gb: 0, total_gb: 0, percent: 0 },
    hostname: 'web',
    uptime_seconds: 0,
    platform: '',
    release: '',
    arch: '',
    docker: null,
  };
}

// 将后端原始 /api/system/stats 响应规整为前端统一契约（三端共用，杜绝字段漂移）
export function normalizeSystemStats(s: any): LiveStats {
  if (!s || typeof s !== 'object') return emptyLiveStats();
  return {
    cpu_percent: Number(s.cpu ?? 0) || 0,
    memory_used_gb: Number(s.ram?.used ?? 0) || 0,
    memory_total_gb: Number(s.ram?.total ?? 0) || 0,
    memory_percent: Number(s.ram?.percent ?? 0) || 0,
    cpu_count: Number(s.cpus ?? 0) || 0,
    temperatures: [],
    fan_speed_rpm: null,
    disk: Array.isArray(s.disk)
      ? s.disk.map((d: any) => ({
          mount: d.mount || '',
          used_gb: Number(d.used_gb ?? 0) || 0,
          total_gb: Number(d.total_gb ?? 0) || 0,
          free_gb: Number(d.free_gb ?? 0) || 0,
          percent: Number(d.percent ?? 0) || 0,
        }))
      : [],
    loadavg:
      Array.isArray(s.loadavg) && s.loadavg.length === 3
        ? [Number(s.loadavg[0]) || 0, Number(s.loadavg[1]) || 0, Number(s.loadavg[2]) || 0]
        : [0, 0, 0],
    net:
      s.net && typeof s.net === 'object'
        ? { rx_bytes: Number(s.net.rx_bytes ?? 0) || 0, tx_bytes: Number(s.net.tx_bytes ?? 0) || 0 }
        : { rx_bytes: 0, tx_bytes: 0 },
    swap:
      s.swap && typeof s.swap === 'object'
        ? { used_gb: Number(s.swap.used_gb ?? 0) || 0, total_gb: Number(s.swap.total_gb ?? 0) || 0, percent: Number(s.swap.percent ?? 0) || 0 }
        : { used_gb: 0, total_gb: 0, percent: 0 },
    hostname: s.hostname ?? 'web',
    uptime_seconds: Number(s.uptime ?? 0) || 0,
    platform: String(s.platform ?? ''),
    release: String(s.release ?? ''),
    arch: String(s.arch ?? ''),
    docker: s.docker && typeof s.docker === 'object'
      ? {
          containers: Array.isArray(s.docker.containers)
            ? s.docker.containers.map((c: any) => ({
                name: String(c.name ?? ''),
                image: String(c.image ?? ''),
                cpu_percent: String(c.cpu_percent ?? ''),
                mem_usage: String(c.mem_usage ?? ''),
                uptime: String(c.uptime ?? ''),
              }))
            : [],
          count: Number(s.docker.count ?? 0) || 0,
        }
      : null,
  };
}
